import React from 'react';
import { motion } from 'framer-motion';
import { getSatisfactionLevel, calculateBuybackPrice } from '../../game/systems/corporate';
import { buttonHover, buttonTap } from '../../ui/animations';

const SATISFACTION_COLORS = {
  satisfied: 'var(--green)',
  concerned: 'var(--yellow)',
  hostile: '#e07040',
  takeover: 'var(--red)',
};

const SATISFACTION_LABELS = {
  satisfied: '✅ Satisfied',
  concerned: '⚠️ Concerned',
  hostile: '😤 Hostile',
  takeover: '🚨 Takeover Threat',
};

export function CorporatePanel({ state, dispatch }) {
  if (!state.corporateInvestmentAccepted || state.corporateBoughtOut) return null;

  const level = getSatisfactionLevel(state.corporateSatisfaction);
  const color = SATISFACTION_COLORS[level];
  const label = SATISFACTION_LABELS[level];
  const buybackPrice = calculateBuybackPrice(state);
  const canBuyback = state.cash >= buybackPrice;
  const directive = state.corporateDirective;
  const restriction = state.corporateChannelRestriction;

  // Days remaining on directive deadline
  const directiveDaysLeft = directive
    ? Math.max(0, directive.deadline - state.gameDay)
    : 0;

  // Directive progress as percentage
  const directivePct = directive && directive.target > 0
    ? Math.min(100, Math.round((directive.progress / directive.target) * 100))
    : directive?.type === 'no_blackmarket' && directive?.progress >= 0 ? 100 : 0;

  return (
    <div className="panel" style={{ borderColor: level === 'takeover' ? 'var(--red)' : level === 'hostile' ? '#e07040' : undefined }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 18 }}>💼</span>
          <span style={{ fontWeight: 700, fontSize: 13 }}>CannCorp Inc.</span>
          <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>25% stake</span>
        </div>
        <span style={{ fontSize: 11, fontWeight: 700, color }}>Q{Math.floor(state.gameWeek / 13) + 1}</span>
      </div>

      {/* Satisfaction gauge */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 4 }}>
          <span style={{ color: 'var(--text-muted)' }}>Board Satisfaction</span>
          <span style={{ color, fontWeight: 700 }}>{label}</span>
        </div>
        <div style={{ height: 6, background: 'var(--border-light)', borderRadius: 3, overflow: 'hidden' }}>
          <motion.div
            style={{ height: '100%', background: color, borderRadius: 3 }}
            initial={{ width: 0 }}
            animate={{ width: `${state.corporateSatisfaction}%` }}
            transition={{ duration: 0.4 }}
          />
        </div>
        <div style={{ fontSize: 10, color: 'var(--text-dim)', textAlign: 'right', marginTop: 2 }}>
          {Math.round(state.corporateSatisfaction)}/100
        </div>
      </div>

      {/* Takeover defense */}
      {level === 'takeover' && (() => {
        const takeoverEvent = state.activeEvents?.find(e => e.type === 'corporate_takeover' && !e.resolved);
        return takeoverEvent ? (
          <div style={{ background: '#1a0505', border: '1px solid var(--red)', borderRadius: 'var(--radius-sm)', padding: '8px 10px', marginBottom: 10 }}>
            <div style={{ fontSize: 12, color: 'var(--red)', fontWeight: 700, marginBottom: 4 }}>🚨 Takeover Imminent</div>
            <div style={{ fontSize: 11, color: '#e8a0a0', marginBottom: 8, lineHeight: 1.4 }}>
              Defend your operation with $500,000 within {Math.max(0, takeoverEvent.deadline - state.gameDay)} days.
            </div>
            <motion.button
              className="btn btn-danger"
              style={{ width: '100%', fontSize: 12 }}
              disabled={state.cash < 500000}
              onClick={() => dispatch({ type: 'DEFEND_TAKEOVER' })}
              whileHover={state.cash >= 500000 ? buttonHover : {}}
              whileTap={state.cash >= 500000 ? buttonTap : {}}
            >
              💰 Defend — Pay $500k
            </motion.button>
          </div>
        ) : null;
      })()}

      {/* Channel restriction */}
      {restriction && restriction.until > state.gameDay && (
        <div style={{ background: 'var(--border-light)', border: '1px solid var(--yellow)', borderRadius: 'var(--radius-sm)', padding: '6px 10px', marginBottom: 10, fontSize: 11 }}>
          <span style={{ color: 'var(--yellow)', fontWeight: 700 }}>🚫 Channel Ban: </span>
          <span style={{ color: 'var(--text)' }}>{restriction.channel}</span>
          <span style={{ color: 'var(--text-muted)', marginLeft: 4 }}>
            ({Math.max(0, restriction.until - state.gameDay)}d left)
          </span>
        </div>
      )}

      {/* Active directive */}
      {directive ? (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>
            Q{directive.quarterNum} Directive
            <span style={{ float: 'right', color: directiveDaysLeft <= 14 ? 'var(--red)' : 'var(--text-dim)' }}>
              {directiveDaysLeft}d left
            </span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text)', marginBottom: 6, lineHeight: 1.4 }}>
            {directive.description}
          </div>
          <div style={{ height: 5, background: 'var(--border-light)', borderRadius: 3, overflow: 'hidden', marginBottom: 2 }}>
            <div
              style={{ width: `${directivePct}%`, height: '100%', background: directivePct >= 100 ? 'var(--green)' : 'var(--blue)', borderRadius: 3, transition: 'width 0.3s' }}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text-dim)' }}>
            <span>{directivePct}% complete</span>
            <span style={{ color: directive.penalty === 'fine' ? 'var(--red)' : 'var(--yellow)' }}>
              {directive.penalty === 'fine' ? '⚠ Fine on miss' : '⚠ Rep loss on miss'}
            </span>
          </div>
        </div>
      ) : (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10, fontStyle: 'italic' }}>
          No active directive. New quarter starts soon.
        </div>
      )}

      {/* Missed directives count */}
      {state.corporateDirectiveMissCount > 0 && (
        <div style={{ fontSize: 11, color: 'var(--red)', marginBottom: 8 }}>
          ⚡ {state.corporateDirectiveMissCount} consecutive miss{state.corporateDirectiveMissCount > 1 ? 'es' : ''} — penalties escalating
        </div>
      )}

      {/* Buyback */}
      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 6 }}>
          <span style={{ color: 'var(--text-muted)' }}>Buyback price</span>
          <span style={{ color: canBuyback ? 'var(--green)' : 'var(--text)', fontWeight: 600 }}>
            ${buybackPrice.toLocaleString()}
          </span>
        </div>
        <motion.button
          className="btn btn-secondary"
          style={{ width: '100%', fontSize: 11, padding: '6px 10px' }}
          disabled={!canBuyback}
          onClick={() => dispatch({ type: 'BUYBACK_CORPORATE_EQUITY' })}
          whileHover={canBuyback ? buttonHover : {}}
          whileTap={canBuyback ? buttonTap : {}}
        >
          🏦 Buy Out CannCorp ({canBuyback ? 'Affordable' : `Need $${(buybackPrice - state.cash).toLocaleString()} more`})
        </motion.button>
      </div>
    </div>
  );
}
